import { ReturnSessionResult } from '@cpd-app/core-module/models';
import { User } from '@cpd-app/generated';
import { createAction, props } from '@ngrx/store';

const tokenProp = () => props<{ token: string; refreshToken?: string }>();
const privacyPolicyAcceptedProp = () =>
  props<{ privacyPolicyAccepted?: boolean }>();
const profileWorkTypesetProp = () => props<{ profileWorkTypeSet?: boolean }>();

export const initAuthState = createAction('[Auth] Init');
export const setSessionInitializing = createAction(
  '[Auth] Set Session Initializing',
  props<{ initializing: boolean }>()
);

export const getSession = createAction('[Auth] Get Session', tokenProp());
export const getSessionSuccess = createAction('[Auth] Get Session Success');

// On session restore (refresh / new tab / deep-link), re-hydrate the auth
// state's role + isAuthenticated. Without this they only ever got set by
// adminLogInSuccess, so an admin was silently logged out of /analytics and
// /manager on every page load (LP-4a root cause).
export const restoreAuthorizationState = createAction(
  '[Auth] Restore Authorization State',
  props<{ role: string; isAuthenticated: boolean }>()
);
/*
 * DISTINCT TYPE STRINGS. These two shared '[Auth] Get Session Success' with
 * `getSessionSuccess` above, and in NgRx the string IS the identity: every
 * reducer case and every effect listening for that type ran for all three.
 *
 * This is the same fault as BUG-592, where two actions sharing a string made
 * every pregoal save run the whole-profile-replace handler with an undefined
 * payload and cut the profile down to `{ userHasPreGoal: true }`. Here the
 * live consequence was `sessionSuccess$` firing twice per session restore,
 * once for the dispatch below and once for the real one beside it, each time
 * marking the session initialised and navigating. Nothing yet reads these two
 * payloads, so the sharing was a trap set for whatever read them next.
 */
export const getSessionSuccessWithPrivacyPolicy = createAction(
  '[Auth] Get Session Success With Privacy Policy',
  privacyPolicyAcceptedProp()
);

export const getSessionSuccessProfileWorkTypeSet = createAction(
  '[Auth] Get Session Success Profile Work Type Set',
  profileWorkTypesetProp()
);

export const clearShareToken = createAction('[Auth] Clear Share Token');

export const saveTokenToState = createAction(
  '[Auth] Save Token To State',
  tokenProp()
);

export const getSessionFailed = createAction(
  '[Auth] Get Session Failed',
  props<{ errorMessage: string }>()
);

export const retrySessionFailed = createAction(
  // Historically this shared '[Auth] Get Session Failed' with getSessionFailed,
  // so ofType() fired BOTH effects on one dispatch. Harmless while both emitted
  // the same cleanup, damaging once the branches diverged: the second effect
  // undid the first one's session recovery by signing out of Cognito.
  '[Auth] Retry Session Failed',
  props<{ errorMessage: string }>()
);

export const refreshSessionAndRetryGetSession = createAction(
  '[Auth] Refresh Session And Retry Get Session',
  props<{ error: any }>()
);

export const submitPasscode = createAction(
  '[Auth] Submit Passcode',
  props<{ passcode: string }>()
);

export const submitPasscodeSuccess = createAction(
  '[Auth] Submit Passcode Success'
);

export const submitPasscodeFailure = createAction(
  '[Auth] Submit Passcode Failure'
);

export const adminLogIn = createAction(
  '[Auth] Admin Log In',
  props<{ username: string; password: string }>()
);

export const adminLogInSuccess = createAction(
  '[Auth] Admin Log In Success',
  props<{ user: User }>()
);

export const adminLogInFailure = createAction(
  '[Auth] Admin Log In Failure',
  props<{ error: any }>()
);

export const setAuthProfile = createAction(
  '[Auth] Set Auth Profile',
  props<{ firstName: string; lastName: string }>()
);

export const adminLogOut = createAction('[Auth] Admin Log Out');
export const adminLogOutSuccess = createAction('[Auth] Admin Log Out Success');

export const deleteTokenFromStorage = createAction(
  '[Auth] Delete Token From Storage'
);

export const saveTokenToStorage = createAction(
  '[Auth] Save Token To Storage',
  tokenProp()
);

export const setLoginWallRedirectUrl = createAction(
  '[Auth] Set Login Wall Redirect Url',
  props<{ loginWallRedirectUrl: string }>()
);

export const createReturnSession = createAction(
  '[Auth] Create Return Session',
  props<{ emailAddress: string }>()
);

export const processReturnSessionCompleted = createAction(
  '[Auth] Process Return Session Completed'
);

export const processReturnSession = createAction(
  '[Auth] Process Return Session'
);

export const returnSessionProcessedSuccess = createAction(
  '[Auth] Return Session Processed Success',
  props<{ token: string; returnSessionResult: ReturnSessionResult }>()
);

export const returnSessionProcessedFailure = createAction(
  '[Auth] Return Session Processed Failure',
  props<{ returnSessionResult: ReturnSessionResult }>()
);
